/**
 * Active Test Store -- tracks which test the candidate has chosen on the
 * `/candidate/assessments` list. Every candidate page that fetches a
 * test-scoped endpoint passes this through as `?test_id=`. When unset,
 * the backend falls back to its actionable-participant picker.
 *
 * Persisted to sessionStorage (not localStorage) so a shared browser
 * doesn't carry one user's selection into another user's tab.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface ActiveTestStoreState {
  testId: string | null;
  setTestId: (id: string | null) => void;
  clear: () => void;
}

export const useActiveTestStore = create<ActiveTestStoreState>()(
  persist(
    (set) => ({
      testId: null,
      setTestId: (id) => set({ testId: id }),
      clear: () => set({ testId: null }),
    }),
    {
      name: 'xyden-active-test',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);

/**
 * Helper for non-React contexts (axios interceptors, services). Reads
 * the store snapshot synchronously.
 */
export function getActiveTestId(): string | null {
  return useActiveTestStore.getState().testId;
}

/**
 * Append `?test_id=...` (or `&test_id=...`) to a candidate-API URL when
 * the candidate has selected a specific test. When no test is selected,
 * returns the path unchanged so the backend falls back to its
 * actionable-participant picker — preserving the legacy single-test
 * login flow.
 */
export function withActiveTestId(path: string): string {
  const testId = getActiveTestId();
  if (!testId) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}test_id=${encodeURIComponent(testId)}`;
}
